"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Eye, Pencil, Ban, RotateCcw, Crown } from "lucide-react";
import { ShortId } from "@/components/ui/short-id";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Subscription, Customer, Vehicle } from "@/types";
import { PaginationMeta } from "@/types/appointment.api.types";

interface SubscriptionTableQuery {
  page: number;
  limit: number;
}

interface SubscriptionsTableProps {
  subscriptions: Subscription[];
  customers: Customer[];
  vehicles: Vehicle[];
  getStatusBadge: (status: string) => JSX.Element;
  onView: (subscription: Subscription) => void;
  onEdit?: (subscription: Subscription) => void;
  onExpire: (subscription: Subscription) => void;
  onActivate: (subscription: Subscription) => void;
  onToggleAutoRenew: (subscription: Subscription, autoRenew: boolean) => void;
  autoRenewUpdatingIds: string[];
  query: SubscriptionTableQuery;
  pagination: PaginationMeta;
  onQueryChange: (query: SubscriptionTableQuery) => void;
}

export function SubscriptionsTable({
  subscriptions,
  customers,
  vehicles,
  getStatusBadge,
  onView,
  onEdit,
  onExpire,
  onActivate,
  onToggleAutoRenew,
  autoRenewUpdatingIds,
  query,
  pagination,
  onQueryChange,
}: SubscriptionsTableProps) {
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      onQueryChange({ ...query, page });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleLimitChange = (limit: number) => {
    onQueryChange({ ...query, limit, page: 1 });
  };

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const maxVisiblePages = 5;

    if (pagination.totalPages <= maxVisiblePages) {
      for (let i = 1; i <= pagination.totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (pagination.page <= 3) {
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(pagination.totalPages);
      } else if (pagination.page >= pagination.totalPages - 2) {
        pages.push("ellipsis");
        for (let i = pagination.totalPages - 3; i <= pagination.totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push("ellipsis");
        for (let i = pagination.page - 1; i <= pagination.page + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(pagination.totalPages);
      }
    }

    return pages;
  };

  const startIndex = pagination.total > 0
    ? (pagination.page - 1) * pagination.limit + 1
    : 0;
  const endIndex = Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            Subscriptions ({pagination.total || subscriptions.length})
          </CardTitle>
          {pagination.total > 0 && (
            <div className="text-sm text-muted-foreground">
              Showing {startIndex} - {endIndex} of {pagination.total}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Monthly Price</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Re-subscribe</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center text-muted-foreground"
                  >
                    No subscriptions found
                  </TableCell>
                </TableRow>
              ) : (
                subscriptions.map((subscription) => {
                  // Use customer data from subscription if available and valid, otherwise lookup from array
                  // Backend might return "Unknown Customer" - in that case, use customers array lookup
                  const backendCustomerName = subscription.customerName;
                  const isValidCustomerName =
                    backendCustomerName &&
                    backendCustomerName.trim() !== "" &&
                    backendCustomerName.toLowerCase() !== "unknown customer";

                  const customerName = isValidCustomerName
                    ? backendCustomerName
                    : customers.find((c) => c.id === subscription.customerId)
                        ?.name || "N/A";

                  // Use vehicle data from subscription if available, otherwise lookup from array
                  let vehicleDisplay = "N/A";

                  if (subscription.vehicleMake && subscription.vehicleModel) {
                    // Use vehicle data directly from subscription
                    vehicleDisplay = `${subscription.vehicleMake} ${subscription.vehicleModel}`;
                  } else if (subscription.vehicleId) {
                    // Lookup vehicle by vehicleId
                    const vehicle = vehicles.find(
                      (v) => v.id === subscription.vehicleId,
                    );
                    if (vehicle) {
                      vehicleDisplay = `${vehicle.make} ${vehicle.model}`;
                    }
                  }

                  // If still N/A and we have customerId, try to find any vehicle for this customer
                  if (vehicleDisplay === "N/A" && subscription.customerId) {
                    const customerVehicles = vehicles.filter(
                      (v) => v.customerId === subscription.customerId,
                    );
                    if (customerVehicles.length > 0) {
                      // Use the first vehicle found for this customer
                      const firstVehicle = customerVehicles[0];
                      vehicleDisplay = `${firstVehicle.make} ${firstVehicle.model}`;
                    }
                  }

                  return (
                    <TableRow key={subscription.id}>
                      <TableCell>
                        <ShortId
                          reference={subscription.subscriptionNumber}
                          id={subscription.id}
                          label="Subscription ID"
                        />
                      </TableCell>
                      <TableCell>{customerName}</TableCell>
                      <TableCell>{vehicleDisplay}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{subscription.planName}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {(subscription.paymentDueAmount !== undefined && (subscription.paymentDiscount !== 0 || subscription.paymentPlatinumDiscount !== 0)) ? (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span>{formatCurrency(subscription.paymentDueAmount || subscription.monthlyPrice)}</span>
                              {(subscription.paymentPlatinumDiscount ?? 0) > 0 && (
                                <Badge className="text-xs"><Crown className="h-3 w-3 text-amber-500" /></Badge>
                              )}
                              {subscription.paymentDiscount !== undefined && subscription.paymentDiscount < 0 && (
                                <Badge className="bg-red-100 text-red-700 text-xs">+{formatCurrency(Math.abs(subscription.paymentDiscount || 0))} surcharge</Badge>
                              )}
                              {subscription.paymentDiscount !== undefined && subscription.paymentDiscount > 0 && (
                                <Badge className="bg-green-100 text-green-700 text-xs">-{formatCurrency(subscription.paymentDiscount || 0)} adj.</Badge>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground line-through">
                              {formatCurrency(subscription.monthlyPrice)}
                            </span>
                          </div>
                        ) : (
                          formatCurrency(subscription.monthlyPrice)
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDate(subscription.startDate)}
                      </TableCell>
                      <TableCell>
                        {subscription.recurringDay}
                        {subscription.recurringDay2 &&
                          ` & ${subscription.recurringDay2}`}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(subscription.status)}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={subscription.autoRenew ?? false}
                          onCheckedChange={(checked) =>
                            onToggleAutoRenew(subscription, checked)
                          }
                          disabled={autoRenewUpdatingIds.includes(
                            subscription.id,
                          )}
                          aria-label="Re-subscribe"
                          title="Automatically create a new subscription after 30 days"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onView(subscription)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {onEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onEdit(subscription)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {subscription.status === "active" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onExpire(subscription)}
                              title="Expire subscription"
                            >
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                          {subscription.status === "expired" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onActivate(subscription)}
                              title="Activate subscription"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Items per page:</span>
              <Select
                value={String(query.limit || 10)}
                onValueChange={(value) => handleLimitChange(Number(value))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (pagination.hasPrev) {
                        handlePageChange(pagination.page - 1);
                      }
                    }}
                    className={
                      !pagination.hasPrev
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>

                {getPageNumbers().map((page, index) => {
                  if (page === "ellipsis") {
                    return (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }

                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(page);
                        }}
                        isActive={pagination.page === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (pagination.hasNext) {
                        handlePageChange(pagination.page + 1);
                      }
                    }}
                    className={
                      !pagination.hasNext
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
